import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, LogIn, Zap, Shield, TrendingUp, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import logo from '../components/assets/logo.png';

export const Login = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Set authentication in sessionStorage
    sessionStorage.setItem('isAuthenticated', 'true');
    sessionStorage.setItem('userEmail', formData.email);
    
    toast({
      title: "Login Successful!",
      description: "Welcome back to Max Bien Dashboard",
    });
    setTimeout(() => {
      navigate('/admin/dashboard');
    }, 1000);
  };

  return (
    <div className="min-h-screen flex relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#00a8ff] rounded-full blur-3xl opacity-10 animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#4cd3ff] rounded-full blur-3xl opacity-10 animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative z-10 p-12 flex-col justify-center">
        <div className="max-w-xl mx-auto">
          <div className="mb-8 flex items-center space-x-4">
            <img
              src={logo}
              alt="Max Bien Logo"
              className="h-20 animate-pulse-scale"
              style={{ filter: 'brightness(0) saturate(100%) invert(61%) sepia(98%) saturate(3180%) hue-rotate(175deg) brightness(103%) contrast(101%) drop-shadow(0 0 20px #00a8ff)' }}
             width={1200} height={800} />
            <div>
              <h1 className="text-4xl font-bold led-text animate-led-pulse" style={{ textShadow: '0 0 15px #00a8ff, 0 0 30px #4cd3ff' }}>
                MAX BIEN
              </h1>
              <p className="text-[#00a8ff] text-lg" style={{ textShadow: '0 0 8px #00a8ff' }}>
                Software Solutions
              </p>
            </div>
          </div>

          <div className="space-y-8">
            <div>
              <h2 className="text-3xl font-bold text-white mb-4" style={{ textShadow: '0 0 10px #00a8ff' }}>
                Welcome to the Future of Digital Innovation
              </h2>
              <p className="text-[#00a8ff] text-lg leading-relaxed" style={{ textShadow: '0 0 5px #00a8ff' }}>
                Access your powerful admin dashboard to manage clients, projects, and business operations.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Zap, title: 'Fast Performance', desc: 'Lightning-fast response' },
                { icon: Shield, title: 'Secure Access', desc: 'Bank-level encryption' },
                { icon: TrendingUp, title: 'Analytics', desc: 'Real-time insights' },
                { icon: Award, title: 'Support', desc: '24/7 assistance' }
              ].map((feature, idx) => (
                <Card key={idx} className="bg-[rgba(30,64,98,0.7)] border border-[#00a8ff] hover:shadow-[0_0_20px_#00a8ff] transition-all duration-300">
                  <CardContent className="p-4">
                    <feature.icon className="text-[#00a8ff] mb-2" size={24} />
                    <h3 className="text-white font-bold text-sm mb-1">{feature.title}</h3>
                    <p className="text-[#00a8ff] text-xs">{feature.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="flex justify-around py-6 border-t border-b border-[#00a8ff]">
              {[
                { value: '250+', label: 'Clients' },
                { value: '1000+', label: 'Projects' },
                { value: '99.9%', label: 'Uptime' }
              ].map((stat, idx) => (
                <div key={idx} className="text-center">
                  <div className="text-3xl font-bold led-text" style={{ textShadow: '0 0 15px #00a8ff' }}>{stat.value}</div>
                  <div className="text-[#00a8ff] text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4 lg:p-12 relative z-10">
        <div className="w-full max-w-md animate-slide-up">
          <div className="lg:hidden text-center mb-8">
            <img
              src={logo}
              alt="Max Bien Logo"
              className="h-16 mx-auto mb-4"
              style={{ filter: 'brightness(0) saturate(100%) invert(61%) sepia(98%) saturate(3180%) hue-rotate(175deg) brightness(103%) contrast(101%) drop-shadow(0 0 15px #00a8ff)' }}
             width={1200} height={800} />
            <h1 className="text-3xl font-bold led-text">MAX BIEN</h1>
          </div>

          <Card className="bg-[rgba(30,64,98,0.9)] border-2 border-[#00a8ff] rounded-2xl backdrop-blur-xl shadow-[0_0_40px_rgba(0,168,255,0.5)]">
            <CardContent className="p-8 lg:p-10">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-white mb-2" style={{ textShadow: '0 0 10px #00a8ff' }}>
                  Welcome Back!
                </h2>
                <p className="text-[#00a8ff]">Sign in to access your dashboard</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="email" className="text-[#00a8ff] font-bold">Email Address</Label>
                  <div className="relative mt-2">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#00a8ff]" size={20} />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="pl-12 bg-[rgba(10,37,64,0.7)] border-[#00a8ff] text-white"
                      placeholder="admin@maxbien.com"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="password" className="text-[#00a8ff] font-bold">Password</Label>
                  <div className="relative mt-2">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#00a8ff]" size={20} />
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handleChange}
                      required
                      className="pl-12 pr-12 bg-[rgba(10,37,64,0.7)] border-[#00a8ff] text-white"
                      placeholder="Enter password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#00a8ff]"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <label className="flex items-center space-x-2">
                    <input type="checkbox" className="w-4 h-4 rounded" />
                    <span className="text-sm text-[#00a8ff]">Remember me</span>
                  </label>
                  <a href="#" className="text-sm text-[#00a8ff] hover:text-[#4cd3ff]">Forgot password?</a>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#00a8ff] text-[#0a2540] hover:bg-[#4cd3ff] font-bold py-6"
                  style={{ boxShadow: '0 0 20px #00a8ff' }}
                >
                  <LogIn className="mr-2" size={20} />
                  Sign In
                </Button>
              </form>

              <div className="mt-6 text-center">
                <Link to="/" className="text-sm text-[#00a8ff] hover:text-[#4cd3ff]">
                  ← Back to Home
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
